import { RootState } from 'modules/store';
import { ErrorObject } from 'types/global';
import Errors from 'modules/shared/error/errors';
import { createAsyncThunk } from '@reduxjs/toolkit';
import Message from 'view/shared/message';
import QueryString from 'query-string';
import {
  IbanVerificationBulkDetails,
  PaginatedResponse,
  AvsConfiguration,
  AvsError,
  AvsUpdateConfigPayload,
  AvsVerificationData,
  IbanVerificationDetails,
  IbanVerificationRecord,
  VerificationResponse,
  IbanVerificationResponsePayload,
} from './avs.types';
import AvsService from './avs.service';
import { downloadCSVFile } from '../../utils/dowloadCSVFile';

function getPage(): number {
  const queryParams = QueryString.parse(window.location.search);
  return Number(queryParams.page || 1);
}

export const getAvsConfigurations = createAsyncThunk<
  AvsConfiguration[],
  { appToken: string },
  {
    state: RootState;
    rejectValue: AvsError | ErrorObject;
  }
>(
  'avs/get-configurations',
  async ({ appToken }, { getState, rejectWithValue }) => {
    try {
      const { environment: appEnvironment } = getState().root.global;

      return await AvsService.getAvsConfigurations({
        appToken,
        appEnvironment,
      });
    } catch (error: unknown) {
      return rejectWithValue({
        message: Errors.selectMessage(error),
        errorCode: Errors.errorCode(error),
      });
    }
  },
);

export const updateAvsConfigurations = createAsyncThunk<
  unknown,
  {
    appToken: string;
    data: AvsUpdateConfigPayload[];
  },
  {
    state: RootState;
    rejectValue: { message: string } | AvsError | ErrorObject;
  }
>(
  'avs/update-configurations',
  async ({ appToken, data }, { getState, rejectWithValue, dispatch }) => {
    const { environment: appEnvironment } = getState().root.global;
    try {
      const response = await AvsService.updateAvsConfigurations({
        appToken,
        appEnvironment,
        data,
      });

      // re-fetch configuration list after successful update
      dispatch(getAvsConfigurations({ appToken }));

      return response;
    } catch (error: unknown) {
      const genericError =
        'Failed to update Account Verification configurations';
      Message.error(Errors.selectMessage(error) || genericError);

      return rejectWithValue({
        message: Errors.selectMessage(error),
        errorCode: Errors.errorCode(error),
      });
    }
  },
);

export const verifyAccount = createAsyncThunk<
  VerificationResponse<IbanVerificationResponsePayload>,
  {
    appToken: string;
    data: AvsVerificationData;
  },
  {
    state: RootState;
    rejectValue: { message: string } | AvsError | ErrorObject;
  }
>(
  'avs/verify-account',
  async ({ appToken, data }, { getState, rejectWithValue, dispatch }) => {
    const { environment: appEnvironment } = getState().root.global;
    try {
      return await AvsService.verifyAccount({
        appToken,
        appEnvironment,
        data,
      });
    } catch (error: unknown) {
      return rejectWithValue({
        message: Errors.selectMessage(error),
        status: Errors.errorStatusOrStatusCode(error),
        errorCode: Errors.errorCode(error),
      });
    } finally {
      const page = getPage();
      dispatch(getVerificationRecords({ appToken, page }));
    }
  },
);

export const uploadBulkVerification = createAsyncThunk<
  IbanVerificationBulkDetails,
  {
    appToken: string;
    data: any;
  },
  {
    state: RootState;
    rejectValue: { message: string } | AvsError | ErrorObject;
  }
>(
  'avs/bulk-verification',
  async ({ appToken, data }, { getState, rejectWithValue, dispatch }) => {
    const { environment: appEnvironment } = getState().root.global;
    try {
      return await AvsService.uploadBulkVerification({
        appToken,
        appEnvironment,
        data,
      });
    } catch (error: unknown) {
      return rejectWithValue({
        message: Errors.selectMessage(error),
        status: Errors.errorStatusOrStatusCode(error),
        errorCode: Errors.errorCode(error),
      });
    } finally {
      const page = getPage();
      dispatch(getVerificationRecords({ appToken, page }));
    }
  },
);

export const getVerificationRecords = createAsyncThunk<
  PaginatedResponse<IbanVerificationRecord>,
  {
    appToken: string;
    page: number;
  },
  {
    state: RootState;
    rejectValue: { message: string } | AvsError | ErrorObject;
  }
>(
  'avs/get-verification-records',
  async ({ appToken, page }, { getState, rejectWithValue }) => {
    const { environment: appEnvironment } = getState().root.global;
    try {
      return await AvsService.getVerificationRecords({
        appToken,
        appEnvironment,
        page,
      });
    } catch (error: unknown) {
      return rejectWithValue({
        message: Errors.selectMessage(error),
        errorCode: Errors.errorCode(error),
      });
    }
  },
);

export const getBulkVerificationResult = createAsyncThunk<
  IbanVerificationBulkDetails,
  {
    appToken: string;
    requestId: string;
  },
  {
    state: RootState;
    rejectValue: { message: string } | AvsError | ErrorObject;
  }
>(
  'avs/get-bulk-verification-result',
  async ({ appToken, requestId }, { getState, rejectWithValue }) => {
    const { environment: appEnvironment } = getState().root.global;
    try {
      return await AvsService.getBulkVerificationResult({
        appToken,
        requestId,
        appEnvironment,
      });
    } catch (error: unknown) {
      return rejectWithValue({
        message: Errors.selectMessage(error),
        errorCode: Errors.errorCode(error),
      });
    }
  },
);

export const getBulkVerificationResultData = createAsyncThunk<
  PaginatedResponse<IbanVerificationDetails>,
  {
    appToken: string;
    requestId: string;
    isCsvFile?: boolean;
    page?: number;
  },
  {
    state: RootState;
    rejectValue: { message: string } | AvsError | ErrorObject;
  }
>(
  'avs/get-bulk-verification-result-data',
  async (
    { appToken, requestId, isCsvFile, page },
    { getState, rejectWithValue },
  ) => {
    const { environment: appEnvironment } = getState().root.global;
    try {
      const response = await AvsService.getBulkVerificationResultData({
        appToken,
        requestId,
        isCsvFile,
        appEnvironment,
        page,
      });

      if (isCsvFile) {
        downloadCSVFile(response, `iban_verification_${requestId}.csv`);
      }
      return response;
    } catch (error: unknown) {
      return rejectWithValue({
        message: Errors.selectMessage(error),
        errorCode: Errors.errorCode(error),
      });
    }
  },
);

export const getIbanVerificationResult = createAsyncThunk<
  IbanVerificationDetails,
  {
    appToken: string;
    requestId: string;
    isCsvFile?: boolean;
  },
  {
    state: RootState;
    rejectValue: { message: string } | AvsError | ErrorObject;
  }
>(
  'avs/get-iban-verification-result',
  async ({ appToken, requestId, isCsvFile }, { getState, rejectWithValue }) => {
    const { environment: appEnvironment } = getState().root.global;
    try {
      const response = await AvsService.getIbanVerificationResult({
        appToken,
        requestId,
        isCsvFile,
        appEnvironment,
      });

      if (isCsvFile) {
        downloadCSVFile(response, `iban_verification_${requestId}.csv`);
      }
      return response;
    } catch (error: unknown) {
      return rejectWithValue({
        message: Errors.selectMessage(error),
        errorCode: Errors.errorCode(error),
      });
    }
  },
);
